import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import PropertySection from "../PropertySection";
import { VIDEO_PRELOAD_OPTIONS } from "@/constants/property-panel.constants";
import type { VideoSettings } from "../hooks/usePropertyPanelState";

interface VideoSettingsSectionProps {
    videoSettings: VideoSettings;
    onSettingChange: (key: keyof VideoSettings, value: boolean | string) => void;
    disabled?: boolean;
}

const BOOLEAN_TOGGLES: { key: keyof VideoSettings; label: string }[] = [
    { key: "autoplay", label: "Autoplay" },
    { key: "muted", label: "Muted" },
    { key: "loop", label: "Loop" },
    { key: "controls", label: "Controls" },
    { key: "playsInline", label: "Plays Inline" },
];

export const VideoSettingsSection = ({
    videoSettings,
    onSettingChange,
    disabled = false,
}: VideoSettingsSectionProps): React.ReactElement => {
    return (
        <PropertySection title="Video Settings">
            <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                    {BOOLEAN_TOGGLES.map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground">
                                {label}
                            </Label>
                            <Switch
                                checked={videoSettings[key] as boolean}
                                onCheckedChange={(checked) =>
                                    onSettingChange(key, checked)
                                }
                                disabled={disabled}
                            />
                        </div>
                    ))}
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Poster URL
                    </Label>
                    <Input
                        className="w-full h-8 bg-background border-border text-foreground text-xs"
                        value={videoSettings.poster}
                        onChange={(e) =>
                            onSettingChange("poster", e.target.value)
                        }
                        disabled={disabled}
                        placeholder="Enter poster image URL..."
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label className="text-xs text-muted-foreground">
                        Preload
                    </Label>
                    <Select
                        value={videoSettings.preload}
                        onValueChange={(value) =>
                            onSettingChange("preload", value)
                        }
                        disabled={disabled}>
                        <SelectTrigger className="w-full h-8 bg-background border-border text-foreground text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                            {VIDEO_PRELOAD_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option.charAt(0).toUpperCase() +
                                        option.slice(1)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </PropertySection>
    );
};
